import { LitElement, html, css } from 'lit';
import { designSystem } from './styles/design-system.js';
import { authService } from './services/auth-service.js';

// Import login page (always needed)
import './components/login-page.js';

// Import dashboard components (lazy — only when authenticated)
import './components/app-shell.js';
import './components/modern-dashboard.js';
import './components/live-calls-monitor.js';
import './components/call-history.js';
import './components/analytics-dashboard.js';
import './components/agents-manager.js';
import './components/settings-panel.js';
import './components/phone-numbers.js';
import './components/knowledge-base.js';
import './components/campaigns.js';
import './components/contacts.js';

/**
 * Voice AI Dashboard — Main Application
 * Routes between login and dashboard based on auth state
 */
export class VoiceDashboard extends LitElement {
  static properties = {
    currentPage: { type: String },
    connected: { type: Boolean },
    stats: { type: Object },
    activeCalls: { type: Array },
    eventSource: { type: Object },
    _authenticated: { state: true },
    _authChecked: { state: true },
    _user: { state: true }
  };

  static styles = [
    designSystem,
    css`
      :host {
        display: block;
        width: 100%;
        min-height: 100vh;
      }

      :host([authenticated]) {
        min-height: auto;
        height: calc(100vh - 32px);
        background-color: #FFFFFF;
        border-radius: 24px;
        box-shadow:
          0 0 0 1px rgba(28, 25, 23, 0.06),
          0 24px 64px -12px rgba(28, 25, 23, 0.15),
          0 8px 20px -4px rgba(28, 25, 23, 0.08);
        overflow: hidden;
        position: relative;
        z-index: 1;
      }
    `
  ];

  constructor() {
    super();
    this.currentPage = 'dashboard';
    this.connected = false;
    this.stats = {
      activeCalls: 0,
      totalToday: 0,
      avgDuration: 0,
      successRate: 0
    };
    this.activeCalls = [];
    this.eventSource = null;
    this._authenticated = false;
    this._authChecked = false;
    this._user = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._checkAuth();

    // Listen for auth changes
    this._authUnsub = authService.onChange(({ authenticated, user }) => {
      this._authenticated = authenticated;
      this._user = user;
      if (authenticated) {
        this.setAttribute('authenticated', '');
        this.initializeEventStream();
        this.loadInitialData();
      } else {
        this.removeAttribute('authenticated');
        this.closeEventStream();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.closeEventStream();
    if (this._authUnsub) this._authUnsub();
  }

  async _checkAuth() {
    if (authService.isAuthenticated()) {
      // Validate token with server
      const result = await authService.validateToken();
      this._authenticated = result.valid;
      this._user = result.user || authService.getUser();
      if (result.valid) {
        this.setAttribute('authenticated', '');
        this.initializeEventStream();
        this.loadInitialData();
      }
    }
    this._authChecked = true;
  }

  _handleLoginSuccess(e) {
    this._authenticated = true;
    this._user = e.detail.user;
    this.setAttribute('authenticated', '');
    this.initializeEventStream();
    this.loadInitialData();
  }

  initializeEventStream() {
    try {
      // TODO: Connect to real SSE endpoint with Bearer token
      this.connected = true;
      console.log('Event stream initialized');
    } catch (error) {
      console.error('Failed to initialize event stream:', error);
      this.connected = false;
    }
  }

  closeEventStream() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      this.connected = false;
    }
  }

  async loadInitialData() {
    try {
      // Mock data
      this.stats = {
        activeCalls: 3,
        totalToday: 127,
        avgDuration: 4.5,
        successRate: 94.2
      };

      this.activeCalls = [
        {
          id: 'call-1',
          phoneNumber: '+1 (555) 123-4567',
          duration: 145,
          status: 'Active',
          agent: 'Park Receptionist',
          sentiment: 'positive',
          callSid: 'CA1234567890abcdef',
          startedAt: new Date().toISOString(),
          transcripts: [
            { speaker: 'agent', content: 'Hello! Welcome to Pine Valley RV Park. How may I assist you today?', timestamp: 1000 },
            { speaker: 'customer', content: "Hi, I'd like to make a reservation for next weekend.", timestamp: 3000 }
          ]
        },
        {
          id: 'call-2',
          phoneNumber: '+1 (555) 987-6543',
          duration: 67,
          status: 'Active',
          agent: 'Park Receptionist',
          sentiment: 'neutral',
          callSid: 'CA9876543210fedcba',
          startedAt: new Date(Date.now() - 67000).toISOString(),
          transcripts: [
            { speaker: 'agent', content: 'Welcome! What can I help you with?', timestamp: 1000 }
          ]
        },
        {
          id: 'call-3',
          phoneNumber: '+1 (555) 456-7890',
          duration: 312,
          status: 'Active',
          agent: 'After Hours Bot',
          sentiment: 'positive',
          callSid: 'CA4567890123456789',
          startedAt: new Date(Date.now() - 312000).toISOString(),
          transcripts: []
        }
      ];
    } catch (error) {
      console.error('Failed to load initial data:', error);
    }
  }

  handleNavigation(e) {
    this.currentPage = e.detail.page;
  }

  renderPage() {
    switch (this.currentPage) {
      case 'dashboard':
        return html`<modern-dashboard .stats="${this.stats}" .activeCalls="${this.activeCalls}"></modern-dashboard>`;
      case 'live-calls':
        return html`<live-calls-monitor .calls="${this.activeCalls}"></live-calls-monitor>`;
      case 'history':
        return html`<call-history></call-history>`;
      case 'analytics':
        return html`<analytics-dashboard></analytics-dashboard>`;
      case 'agents':
        return html`<agents-manager></agents-manager>`;
      case 'settings':
        return html`<settings-panel></settings-panel>`;
      case 'phone-numbers':
        return html`<phone-numbers></phone-numbers>`;
      case 'knowledge-base':
        return html`<knowledge-base></knowledge-base>`;
      case 'campaigns':
        return html`<voice-campaigns></voice-campaigns>`;
      case 'contacts':
        return html`<voice-contacts></voice-contacts>`;
      default:
        return html`<modern-dashboard .stats="${this.stats}" .activeCalls="${this.activeCalls}"></modern-dashboard>`;
    }
  }

  render() {
    // Still checking auth — show nothing (loading screen still visible)
    if (!this._authChecked) return html``;

    // Not authenticated — show login
    if (!this._authenticated) {
      return html`<login-page @login-success="${this._handleLoginSuccess}"></login-page>`;
    }

    // Authenticated — show dashboard
    return html`
      <app-shell
        .currentPage="${this.currentPage}"
        .connected="${this.connected}"
        .activeCalls="${this.activeCalls.length}"
        .user="${this._user}"
        @navigate="${this.handleNavigation}"
        @logout="${() => authService.logout()}"
      >
        ${this.renderPage()}
      </app-shell>
    `;
  }
}

customElements.define('voice-dashboard', VoiceDashboard);

// Remove loading screen
const hideLoading = () => {
  const el = document.getElementById('appLoading');
  if (el) el.classList.add('hidden');
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hideLoading);
} else {
  hideLoading();
}

console.log('Voice AI System initialized');
